import { useState } from "react";
import { useOutletContext, useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, Circle, Clock, LifeBuoy, Play } from "lucide-react";
import { toEmbedUrl, isLocalMedia, mediaSrc } from "../../lib/embed";
import api from "../../lib/api";
import { FadeUp } from "../../components/site/Reveal";

export default function LessonPage() {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const { content, progress, toggle } = useOutletContext();
  const [openStuck, setOpenStuck] = useState("");
  const m = content.modules.find((x) => x.id === moduleId);
  if (!m) return <p className="text-slate-400">Module not found.</p>;
  const idx = m.lessons.findIndex((l) => l.id === lessonId);
  const lesson = m.lessons[idx];
  if (!lesson) return <p className="text-slate-400">Lesson not found.</p>;

  const itemId = `${m.id}:${lesson.id}`;
  const done = progress.completed.includes(itemId);
  const embed = toEmbedUrl(lesson.video_url);
  const helpEmbed = lesson.help_video ? toEmbedUrl(lesson.help_video.video_url) : null;
  const prev = m.lessons[idx - 1];
  const next = m.lessons[idx + 1];

  const markDone = async () => {
    await toggle(itemId, true);
    if (next) navigate(`/hub/modules/${m.id}/lessons/${next.id}`);
    else navigate(`/hub/modules/${m.id}`);
  };

  return (
    <div data-testid="lesson-page" className="max-w-3xl">
      <FadeUp>
        <Link to={`/hub/modules/${m.id}`} data-testid="lesson-back-link"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors">
          <ArrowLeft size={15} /> {m.code} — {m.title}
        </Link>
        <div className="flex items-center gap-3 mt-5">
          <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{m.code} · LESSON {idx + 1}/{m.lessons.length}]</p>
          <span className="font-mono text-[10px] text-slate-500 flex items-center gap-1 uppercase tracking-widest">
            <Clock size={11} /> {lesson.minutes} min
          </span>
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl mt-3">{lesson.title}</h1>
      </FadeUp>

      <FadeUp delay={0.1} className="mt-7">
        {isLocalMedia(lesson.video_url) ? (
          <video controls playsInline preload="metadata" src={mediaSrc(lesson.video_url)}
            className="w-full aspect-video bg-black border border-white/10" data-testid="lesson-video" />
        ) : embed ? (
          <div className="aspect-video bg-black border border-white/10" data-testid="lesson-video">
            <iframe src={embed} title={lesson.title} className="w-full h-full" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          </div>
        ) : (
          <div className="aspect-video bg-charcoal border border-white/10 grid place-items-center blueprint-grid" data-testid="lesson-video-placeholder">
            <div className="text-center px-6">
              <div className="w-14 h-14 mx-auto bg-safety/15 border border-safety/40 grid place-items-center">
                <Play size={22} className="text-safety ml-0.5" />
              </div>
              <p className="text-slate-300 font-medium mt-4 text-sm">Video walkthrough coming soon</p>
              <p className="text-slate-500 text-xs mt-1">Follow the step-by-step build below — it's the real work anyway.</p>
            </div>
          </div>
        )}
      </FadeUp>

      {lesson.video_meta && (lesson.video_meta.duration || lesson.video_meta.description) && (
        <FadeUp delay={0.15} className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="lesson-video-meta">
          {lesson.video_meta.duration && (
            <span className="font-mono text-[11px] uppercase tracking-widest text-safety">{lesson.video_meta.duration}</span>
          )}
          {lesson.video_meta.description && (
            <span className="text-slate-400 text-sm">{lesson.video_meta.description}</span>
          )}
        </FadeUp>
      )}

      <FadeUp delay={0.2} className="mt-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">[STEP-BY-STEP WALKTHROUGH]</p>
        <div className="space-y-px border border-white/10">
          {lesson.steps.map((s, i) => (
            <div key={i} className="bg-charcoal p-5 flex gap-4" data-testid={`lesson-step-${i + 1}`}>
              <span className="font-mono text-safety text-sm font-bold shrink-0 w-8">0{i + 1}</span>
              <p className="text-slate-200 text-sm leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </FadeUp>

      <FadeUp delay={0.25} className="mt-10" >
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">[GET UNSTUCK]</p>
        {lesson.help_video && (
          <div className="mb-6" data-testid="help-video-block">
            {isLocalMedia(lesson.help_video.video_url) ? (
              <video controls playsInline preload="metadata" src={mediaSrc(lesson.help_video.video_url)}
                className="w-full aspect-video bg-black border border-white/10" data-testid="help-video-player" />
            ) : helpEmbed && helpEmbed !== lesson.help_video.video_url ? (
              <div className="aspect-video bg-black border border-white/10">
                <iframe src={helpEmbed} title={lesson.help_video.title || "Help video"} className="w-full h-full" allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
              </div>
            ) : (
              <a href={lesson.help_video.video_url} target="_blank" rel="noopener noreferrer" data-testid="help-video-link"
                className="inline-flex items-center gap-2 bg-charcoal border border-white/10 hover:border-safety/50 px-5 py-3.5 text-sm text-safety font-semibold transition-colors">
                <Play size={15} /> Watch the quick help video
              </a>
            )}
            <p className="text-slate-400 text-sm mt-2.5">{lesson.help_video.title || "Quick help for this lesson"}</p>
          </div>
        )}
        <div className="border border-white/10" data-testid="stuck-menu">
          <div className="bg-charcoal px-5 py-3.5 flex items-center gap-2.5">
            <LifeBuoy size={15} className="text-safety shrink-0" />
            <p className="text-sm text-slate-200 font-medium">I'm stuck — common obstacles for this lesson</p>
          </div>
          <div className="divide-y divide-white/5">
            {(lesson.stuck_points || []).map((sp) => (
              <div key={sp.id} className="bg-navy">
                <button onClick={() => {
                    const next = openStuck === sp.id ? "" : sp.id;
                    setOpenStuck(next);
                    if (next) api.post("/hub/stuck-view", { lesson_key: `${m.id}:${lesson.id}`, stuck_id: sp.id }).catch(() => {});
                  }} data-testid={`stuck-toggle-${sp.id}`}
                  className="w-full text-left px-5 py-3.5 text-sm text-slate-300 hover:text-white flex justify-between items-center gap-3 transition-colors">
                  {sp.label}
                  <ChevronDown size={15} className={`shrink-0 text-slate-500 transition-transform ${openStuck === sp.id ? "rotate-180" : ""}`} />
                </button>
                {openStuck === sp.id && (
                  <div className="px-5 pb-4" data-testid={`stuck-detail-${sp.id}`}>
                    <p className="text-slate-400 text-sm leading-relaxed">{sp.tip}</p>
                    <Link to={`/hub/help?module=${m.id}&lesson=${lesson.id}&stuck=${encodeURIComponent(sp.label)}`}
                      data-testid={`stuck-ask-${sp.id}`}
                      className="inline-flex items-center gap-2 text-safety hover:text-white text-xs font-semibold mt-3 transition-colors">
                      <LifeBuoy size={13} /> Still stuck? Ask for help with this
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <p className="text-slate-500 text-xs mt-3 leading-relaxed" data-testid="get-unstuck-disclaimer">
          Educational guidance only — you remain responsible for your own business decisions. Verify tax, legal, insurance, or financial choices with a qualified professional.
        </p>
      </FadeUp>

      <FadeUp delay={0.3} className="mt-8 flex flex-wrap gap-3 items-center">
        <button
          onClick={() => toggle(itemId, !done)}
          data-testid="lesson-toggle-complete-btn"
          className={`flex items-center gap-2 font-semibold px-6 py-3.5 text-sm transition-colors ${
            done ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" : "bg-safety hover:bg-safety-hover text-white"
          }`}
        >
          {done ? <CheckCircle2 size={16} /> : <Circle size={16} />}
          {done ? "Completed — tap to undo" : "Mark lesson complete"}
        </button>
        {done && next && (
          <button onClick={markDone} data-testid="lesson-next-btn"
            className="flex items-center gap-2 border border-white/20 hover:border-safety text-white font-semibold px-6 py-3.5 text-sm transition-colors">
            Next lesson <ArrowRight size={15} />
          </button>
        )}
      </FadeUp>

      <FadeUp delay={0.35} className="mt-10 pt-6 border-t border-white/10 flex justify-between gap-4">
        {prev ? (
          <Link to={`/hub/modules/${m.id}/lessons/${prev.id}`} data-testid="lesson-prev-link"
            className="text-slate-400 hover:text-safety text-sm transition-colors">← {prev.title}</Link>
        ) : <span />}
        {next ? (
          <Link to={`/hub/modules/${m.id}/lessons/${next.id}`} data-testid="lesson-next-link"
            className="text-slate-400 hover:text-safety text-sm transition-colors">{next.title} →</Link>
        ) : (
          <Link to={`/hub/modules/${m.id}`} className="text-slate-400 hover:text-safety text-sm transition-colors">
            Back to {m.title} tasks →
          </Link>
        )}
      </FadeUp>
    </div>
  );
}
